import { isAnalyzeExternalWebPageScope } from '@/lib/assistant/analyze-external-web-result-contract';
import { extractDisplayToolReason, TOOL_REASON_UI_MAX_LENGTH } from '@/lib/assistant/tool-input';
import {
  getRunningLabel,
  type RunningLabelValues,
  TOOL_STATUS,
  type ToolState,
} from '@/lib/assistant/tool-status';
import { readBoolean, readTrimmedString } from '@/lib/prop-utils';
import { getSectionTypeLabel } from '@/lib/section-type-icons';
import { capitalize } from '@/lib/utils';
import type { Message } from '@/types/assistant';
import { getSiteScopeWebAnalysisProgressSummary } from './tool-ui-site-analysis';

export const ASSISTANT_TOOL_NAMES = {
  ADD_SECTION_ON_PAGE: 'addSectionOnPage',
  REPLACE_SECTION_ON_PAGE: 'replaceSectionOnPage',
  UPDATE_SECTION_ON_PAGE: 'updateSectionOnPage',
  UPDATE_PAGE_METADATA: 'updatePageMetadata',
  REPOSITION_SECTIONS_ON_PAGE: 'repositionSectionsOnPage',
  DELETE_SECTION_ON_PAGE: 'deleteSectionOnPage',
  SET_SLOT_VISIBILITY: 'setSlotVisibility',
  REJECT_PROPOSAL: 'rejectProposal',
  GET_SECTION_HISTORY: 'getSectionHistory',
  REVERT_ACCEPTED_CHANGES: 'revertAcceptedChanges',
  UPDATE_BLOG_WIZARD_STEP: 'updateBlogWizardStep',
  SUGGEST_BLOG_TOPICS: 'suggestBlogTopics',
  APPLY_BLOG_DRAFT: 'applyBlogDraft',
  ASSIGN_BLOG_AUTHOR: 'assignBlogAuthor',
  RESET_BLOG_DRAFT: 'resetBlogDraft',
  SEARCH_KNOWLEDGE_BASE: 'searchKnowledgeBase',
  WEB_SEARCH: 'webSearch',
  MAP_SITE_URLS: 'mapSiteUrls',
  ANALYZE_EXTERNAL_WEB: 'analyzeExternalWeb',
  GET_CURRENT_PAGE: 'getCurrentPage',
  GET_PAGE_OUTLINE: 'getPageOutline',
  GET_SECTION_DETAIL: 'getSectionDetail',
  GET_INTERNAL_LINKABLE_PAGES: 'getInternalLinkablePages',
  GET_ACCOUNT_INFO: 'getAccountInfo',
  CHOOSE_SECTION_TEMPLATE: 'chooseSectionTemplate',
  GET_SECTION_TEMPLATE_BLUEPRINT: 'getSectionTemplateBlueprint',
} as const;

export type AssistantToolName = (typeof ASSISTANT_TOOL_NAMES)[keyof typeof ASSISTANT_TOOL_NAMES];

export const ASSISTANT_TOOL_NAME_VALUES = Object.values(
  ASSISTANT_TOOL_NAMES,
) as AssistantToolName[];

const INFORMATIONAL_ASSISTANT_TOOL_NAMES = new Set<AssistantToolName>([
  ASSISTANT_TOOL_NAMES.ANALYZE_EXTERNAL_WEB,
  ASSISTANT_TOOL_NAMES.MAP_SITE_URLS,
  ASSISTANT_TOOL_NAMES.GET_CURRENT_PAGE,
  ASSISTANT_TOOL_NAMES.GET_PAGE_OUTLINE,
  ASSISTANT_TOOL_NAMES.GET_SECTION_DETAIL,
  ASSISTANT_TOOL_NAMES.GET_INTERNAL_LINKABLE_PAGES,
  ASSISTANT_TOOL_NAMES.GET_ACCOUNT_INFO,
  ASSISTANT_TOOL_NAMES.SEARCH_KNOWLEDGE_BASE,
  ASSISTANT_TOOL_NAMES.WEB_SEARCH,
  ASSISTANT_TOOL_NAMES.GET_SECTION_TEMPLATE_BLUEPRINT,
  ASSISTANT_TOOL_NAMES.CHOOSE_SECTION_TEMPLATE,
  ASSISTANT_TOOL_NAMES.GET_SECTION_HISTORY,
]);

export function isInformationalAssistantTool(toolName?: string): boolean {
  if (!toolName) {
    return false;
  }

  return INFORMATIONAL_ASSISTANT_TOOL_NAMES.has(toolName as AssistantToolName);
}

export type ToolSummary = {
  label: string;
  subtitle?: string;
};

type ToolSummaryHandler = (
  state: ToolState,
  input: Record<string, unknown>,
  message?: Message,
) => ToolSummary;

type ToolProgressSummaryInput = {
  toolName?: string;
  state: ToolState;
  input?: Record<string, unknown>;
  message?: Message;
};

type SectionMutationVerb = 'add' | 'replace' | 'update' | 'remove';

const BLUEPRINT_TEMPLATE_LABEL_OVERRIDES = new Map<string, string>([
  ['page_footer', 'footer'],
  ['page_header', 'header'],
  ['form_widget', 'contact form'],
  ['faq_widget', 'FAQ'],
]);

const ACCOUNT_RESOURCE_LABELS: Record<string, string> = {
  account: 'account profile',
  preferences: 'saved preferences',
  practiceAreas: 'practice areas',
  site: 'site settings',
};

const DEFAULT_SUMMARY: ToolSummary = {
  label: 'Running tool',
  subtitle: undefined,
};

const toWords = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const toSentence = (items: string[]): string => {
  if (items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const withReasonSubtitle = (summary: ToolSummary, reason?: string): ToolSummary => {
  if (!reason) {
    return summary;
  }

  return {
    ...summary,
    subtitle: reason,
  };
};

const readTrimmedStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => readTrimmedString(item))
    .filter((item): item is string => Boolean(item));
};

const formatBlueprintTemplateLabel = (templateCode: string): string => {
  const normalized = templateCode.trim().toLowerCase();
  const override = BLUEPRINT_TEMPLATE_LABEL_OVERRIDES.get(normalized);
  if (override) {
    return override;
  }

  const firstStem = normalized.split('_')[0];
  const stemLabel = getSectionTypeLabel(firstStem);
  if (stemLabel !== firstStem.replace(/_/g, ' ')) {
    return stemLabel;
  }

  return toWords(normalized.replace(/_widget$/, ''));
};

const SECTION_TARGET = 'section';

const buildSectionMutationLabels = (verb: SectionMutationVerb): RunningLabelValues => {
  const labels = {
    add: ['Preparing to add', 'Adding', 'Added', 'Failed to add'],
    replace: ['Preparing to replace', 'Replacing', 'Replaced', 'Failed to replace'],
    update: ['Preparing to update', 'Updating', 'Updated', 'Failed to update'],
    remove: ['Preparing to remove', 'Removing', 'Removed', 'Failed to remove'],
  }[verb];

  return {
    pending: `${labels[0]} ${SECTION_TARGET}`,
    running: `${labels[1]} ${SECTION_TARGET}`,
    completed: `${labels[2]} ${SECTION_TARGET}`,
    failed: `${labels[3]} ${SECTION_TARGET}`,
  };
};

const formatUnknownToolLabel = (toolName?: string) => {
  if (!toolName) {
    return 'tool';
  }
  return toWords(toolName);
};

const sectionMutationSummary =
  (verb: SectionMutationVerb): ToolSummaryHandler =>
  (state) => ({
    label: getRunningLabel(state, buildSectionMutationLabels(verb)),
  });

const getRepositionSummary: ToolSummaryHandler = (state, input, message) => {
  const moves = Array.isArray(input.moves)
    ? input.moves
    : message?.sectionsRepositionedMetadata?.moves || [];
  const moveCount = moves.length;
  const sectionCount =
    moveCount > 0 ? `${moveCount} section${moveCount === 1 ? '' : 's'}` : 'sections';

  return {
    label: getRunningLabel(state, {
      pending: `Preparing to reorder ${sectionCount}`,
      running: `Reordering ${sectionCount}`,
      completed: `Reordered ${sectionCount}`,
      failed: `Failed to reorder ${sectionCount}`,
    }),
  };
};

const getSetSlotVisibilitySummary: ToolSummaryHandler = (state, input) => {
  const slot = input.slot === 'footer' ? 'footer' : 'header';
  const showing = readBoolean(input.visible);
  const verb = showing ? 'show' : 'hide';

  return {
    label: getRunningLabel(state, {
      pending: `Preparing to ${verb} the ${slot}`,
      running: `${showing ? 'Showing' : 'Hiding'} the ${slot}`,
      completed: `${showing ? 'Showed' : 'Hid'} the ${slot}`,
      failed: `Failed to ${verb} the ${slot}`,
    }),
  };
};

const getRejectProposalSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing to cancel change',
    running: 'Cancelling change',
    completed: 'Change cancelled',
    failed: 'Failed to cancel change',
  }),
});

const getSectionHistorySummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Checking change history',
    running: 'Checking change history',
    completed: 'Change history retrieved',
    failed: 'Failed to retrieve change history',
  }),
});

const getRevertAcceptedChangesSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing to revert accepted changes',
    running: 'Reverting accepted changes',
    completed: 'Revert proposals prepared',
    failed: 'Failed to revert accepted changes',
  }),
});

const getKnowledgeBaseSummary: ToolSummaryHandler = (state) => {
  if (state === TOOL_STATUS.outputAvailable) {
    return {
      label: 'References reviewed',
    };
  }

  return {
    label: getRunningLabel(state, {
      pending: 'Preparing references',
      running: 'Reviewing references',
      completed: 'References reviewed',
      failed: 'Reference review failed',
    }),
  };
};

const getWebSearchSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing web search',
    running: 'Searching for competitors',
    completed: 'Competitor search complete',
    failed: 'Competitor search failed',
  }),
});

const getMapSiteUrlsSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing to map site pages',
    running: 'Finding matching pages on the site',
    completed: 'Found matching site pages',
    failed: 'Could not map site pages',
  }),
});

const getCurrentPageSummary: ToolSummaryHandler = (state, input) => {
  const includeSections = readBoolean(input.includeSections);

  return {
    label: getRunningLabel(state, {
      pending: 'Preparing current page lookup',
      running:
        includeSections === false
          ? 'Reviewing current page details'
          : 'Reviewing current page sections',
      completed: 'Loaded current page context',
      failed: 'Failed to load current page context',
    }),
  };
};

const getAnalyzeExternalWebPageSummary: ToolSummaryHandler = (state, input) => {
  const targetUrl = readTrimmedString(input.url);
  const summary = {
    label: getRunningLabel(state, {
      pending: 'Getting ready to read the page',
      running: 'Reading the page...',
      completed: 'Finished reading the page',
      failed: "Couldn't read that page",
    }),
  };

  return targetUrl ? { ...summary, subtitle: targetUrl } : summary;
};

const getAnalyzeExternalWebSummary: ToolSummaryHandler = (state, input, message) => {
  if (isAnalyzeExternalWebPageScope(input)) {
    return getAnalyzeExternalWebPageSummary(state, input, message);
  }

  return getSiteScopeWebAnalysisProgressSummary({ state, input, message });
};

const getPageOutlineSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing page outline',
    running: 'Reviewing page structure',
    completed: 'Loaded page outline',
    failed: 'Failed to load page outline',
  }),
});

const getSectionDetailSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing section details',
    running: 'Reviewing section details',
    completed: 'Loaded section details',
    failed: 'Failed to load section details',
  }),
});

const getInternalLinkablePagesSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing page list',
    running: 'Finding pages to link to',
    completed: 'Found pages to link to',
    failed: 'Failed to find pages to link to',
  }),
});

const getUnknownToolSummary = (state: ToolState, toolName?: string): ToolSummary => {
  const toolLabel = capitalize(formatUnknownToolLabel(toolName));

  return {
    label: getRunningLabel(state, {
      pending: `Preparing ${toolLabel}`,
      running: `Running ${toolLabel}`,
      completed: `Completed ${toolLabel}`,
      failed: `Failed ${toolLabel}`,
    }),
  };
};

const getAccountInfoSummary: ToolSummaryHandler = (state, input) => {
  const resources = readTrimmedStringArray(input.resources);
  const labels = resources.map((resource) => {
    if (ACCOUNT_RESOURCE_LABELS[resource]) {
      return ACCOUNT_RESOURCE_LABELS[resource];
    }
    return toWords(resource);
  });

  const resourceText = labels.length > 0 ? toSentence(labels) : 'account context';

  return {
    label: getRunningLabel(state, {
      pending: 'Preparing account lookup',
      running: `Loading ${resourceText}`,
      completed: `Loaded ${resourceText}`,
      failed: `Failed to load ${resourceText}`,
    }),
  };
};

const getTemplateBlueprintSummary: ToolSummaryHandler = (state, input) => {
  const templateCode = readTrimmedString(input.sectionTemplateCode);
  const templateLabel = templateCode
    ? `${formatBlueprintTemplateLabel(templateCode)} layout`
    : 'layout structure';

  return {
    label: getRunningLabel(state, {
      pending: 'Preparing layout structure',
      running: `Loading ${templateLabel}`,
      completed: `Loaded ${templateLabel}`,
      failed: `Failed to load ${templateLabel}`,
    }),
  };
};

const getChooseSectionTemplateSummary: ToolSummaryHandler = (state, input) => {
  const sectionType = readTrimmedString(input.sectionType);
  const sectionLabel = sectionType ? `${toWords(sectionType)} layout` : 'layout';

  return {
    label: getRunningLabel(state, {
      pending: `Preparing ${sectionLabel} selection`,
      running: `Choosing ${sectionLabel}`,
      completed: `Chose ${sectionLabel}`,
      failed: `Failed to choose ${sectionLabel}`,
    }),
  };
};

const BLOG_WIZARD_ACTION_LABELS = new Map<string, string>([
  ['choose_practice_area', 'practice area'],
  ['choose_topic', 'topic'],
  ['regenerate_topics', 'topic ideas'],
  ['return_to_topic_selection', 'topic list'],
  ['start_over', 'blog setup'],
  ['assign_author', 'author'],
  ['skip_author', 'author assignment'],
]);

const getUpdateBlogWizardStepSummary: ToolSummaryHandler = (state, input) => {
  const action = readTrimmedString(input.action);
  const target = action ? (BLOG_WIZARD_ACTION_LABELS.get(action) ?? 'blog step') : 'blog step';

  return {
    label: getRunningLabel(state, {
      pending: `Preparing ${target}`,
      running: `Updating ${target}`,
      completed: `Updated ${target}`,
      failed: `Failed to update ${target}`,
    }),
  };
};

const getSuggestBlogTopicsSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing topic ideas',
    running: 'Suggesting topics',
    completed: 'Topics suggested',
    failed: 'Failed to suggest topics',
  }),
});

const getApplyBlogDraftSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing draft',
    running: 'Writing draft',
    completed: 'Draft ready',
    failed: 'Failed to write draft',
  }),
});

const getAssignBlogAuthorSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing to assign author',
    running: 'Assigning author',
    completed: 'Author assigned',
    failed: 'Failed to assign author',
  }),
});

const getResetBlogDraftSummary: ToolSummaryHandler = (state) => ({
  label: getRunningLabel(state, {
    pending: 'Preparing to start over',
    running: 'Starting over',
    completed: 'Blog reset',
    failed: 'Failed to reset blog',
  }),
});

const ASSISTANT_TOOL_UI: Record<AssistantToolName, ToolSummaryHandler> = {
  [ASSISTANT_TOOL_NAMES.ADD_SECTION_ON_PAGE]: sectionMutationSummary('add'),
  [ASSISTANT_TOOL_NAMES.REPLACE_SECTION_ON_PAGE]: sectionMutationSummary('replace'),
  [ASSISTANT_TOOL_NAMES.UPDATE_SECTION_ON_PAGE]: sectionMutationSummary('update'),
  [ASSISTANT_TOOL_NAMES.UPDATE_PAGE_METADATA]: sectionMutationSummary('update'),
  [ASSISTANT_TOOL_NAMES.REPOSITION_SECTIONS_ON_PAGE]: getRepositionSummary,
  [ASSISTANT_TOOL_NAMES.DELETE_SECTION_ON_PAGE]: sectionMutationSummary('remove'),
  [ASSISTANT_TOOL_NAMES.SET_SLOT_VISIBILITY]: getSetSlotVisibilitySummary,
  [ASSISTANT_TOOL_NAMES.REJECT_PROPOSAL]: getRejectProposalSummary,
  [ASSISTANT_TOOL_NAMES.GET_SECTION_HISTORY]: getSectionHistorySummary,
  [ASSISTANT_TOOL_NAMES.REVERT_ACCEPTED_CHANGES]: getRevertAcceptedChangesSummary,
  [ASSISTANT_TOOL_NAMES.UPDATE_BLOG_WIZARD_STEP]: getUpdateBlogWizardStepSummary,
  [ASSISTANT_TOOL_NAMES.SUGGEST_BLOG_TOPICS]: getSuggestBlogTopicsSummary,
  [ASSISTANT_TOOL_NAMES.APPLY_BLOG_DRAFT]: getApplyBlogDraftSummary,
  [ASSISTANT_TOOL_NAMES.ASSIGN_BLOG_AUTHOR]: getAssignBlogAuthorSummary,
  [ASSISTANT_TOOL_NAMES.RESET_BLOG_DRAFT]: getResetBlogDraftSummary,
  [ASSISTANT_TOOL_NAMES.SEARCH_KNOWLEDGE_BASE]: getKnowledgeBaseSummary,
  [ASSISTANT_TOOL_NAMES.WEB_SEARCH]: getWebSearchSummary,
  [ASSISTANT_TOOL_NAMES.MAP_SITE_URLS]: getMapSiteUrlsSummary,
  [ASSISTANT_TOOL_NAMES.ANALYZE_EXTERNAL_WEB]: getAnalyzeExternalWebSummary,
  [ASSISTANT_TOOL_NAMES.GET_CURRENT_PAGE]: getCurrentPageSummary,
  [ASSISTANT_TOOL_NAMES.GET_PAGE_OUTLINE]: getPageOutlineSummary,
  [ASSISTANT_TOOL_NAMES.GET_SECTION_DETAIL]: getSectionDetailSummary,
  [ASSISTANT_TOOL_NAMES.GET_INTERNAL_LINKABLE_PAGES]: getInternalLinkablePagesSummary,
  [ASSISTANT_TOOL_NAMES.GET_ACCOUNT_INFO]: getAccountInfoSummary,
  [ASSISTANT_TOOL_NAMES.CHOOSE_SECTION_TEMPLATE]: getChooseSectionTemplateSummary,
  [ASSISTANT_TOOL_NAMES.GET_SECTION_TEMPLATE_BLUEPRINT]: getTemplateBlueprintSummary,
};

export function isAssistantToolName(name: string | undefined): name is AssistantToolName {
  if (!name) {
    return false;
  }

  const isKnownTool = ASSISTANT_TOOL_NAME_VALUES.includes(name as AssistantToolName);
  if (import.meta.env.DEV && !isKnownTool) {
    console.warn(
      `[isAssistantToolName] Unknown tool "${name}". If this is a new assistant tool, add it to ASSISTANT_TOOL_UI.`,
    );
  }

  return isKnownTool;
}

export function getToolProgressSummary({
  toolName,
  state,
  input = {},
  message,
}: ToolProgressSummaryInput): ToolSummary {
  if (!toolName) {
    return DEFAULT_SUMMARY;
  }

  const reason = extractDisplayToolReason(input, TOOL_REASON_UI_MAX_LENGTH);

  // analyzeExternalWeb: show the target URL (or live scrape progress), never the model reason.
  if (toolName === ASSISTANT_TOOL_NAMES.ANALYZE_EXTERNAL_WEB) {
    return getAnalyzeExternalWebSummary(state, input, message);
  }

  if (isAssistantToolName(toolName)) {
    const handler = ASSISTANT_TOOL_UI[toolName];
    if (!handler) {
      return withReasonSubtitle(getUnknownToolSummary(state, toolName), reason);
    }

    return withReasonSubtitle(handler(state, input, message), reason);
  }

  return withReasonSubtitle(getUnknownToolSummary(state, toolName), reason);
}
